/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {useEffect, useState} from 'react'
import {format} from 'date-fns'
import {useTranslation} from 'react-i18next'
import {Button, makeStyles} from '@material-ui/core'
import PurchaseHistoryListOrganizationStyle from 'pages/ProductHistoryOrganization/ProductHistoryOrganizationList/style'
import CardWithTitle from 'components/atoms/CardWithTitle'
import Pagination from 'components/molcules/Pagination'
import TableToolbar from 'pages/ProductHistoryOrganization/ProductHistoryOrganizationList/TableToolbar'
import DataTable from 'pages/ProductHistoryOrganization/ProductHistoryOrganizationList/DataTable'
import CancelProductDialog from 'components/Dialog/Product/CancelProductDialog/CancelProductDialog'
import {navigate} from 'AppHistory'
import useProductManagement from 'features/productManagement/useProductManagement'
import useFailureModal from 'features/modal/useFailureModal'
import useSuccessModal from 'features/modal/useSuccessModal'
import {formatDate, oneYearAgo} from 'helpers/dateHelper'
import {SEARCH_SELECT_ITEMS} from 'pages/ProductHistoryOrganization/ProductHistoryOrganizationList/ToolbarSearch'
import {
  Payment,
  getRelatedPaymentApi,
  getSalesSlipApi,
  ISalesSlip,
  ProductPurchaseHistoryResponse,
  updateSalesSlipApi,
} from 'api/productApi'
import {Agency, getListAgencyApi} from 'api/orgApi'
import {getListOrgDetailApi} from 'api/salesApi'
import {centToUSD} from 'helpers/paymentHelper'
import {downloadCsv} from 'helpers/csvHelper'
import {AppLoading} from 'AppLoading'
import InvoicePopup from './InvoicePopup'

const useStyles = makeStyles(PurchaseHistoryListOrganizationStyle)
const ProductHistoryOrganizationList = () => {
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const [paymentStatus, setPaymentStatus] = useState('ALL')
  const [isOpenCancelDialog, setIsOpenCancelDialog] = useState(false)
  const [isShowInvoicePopup, setIsShowInvoicePopup] = useState(false)
  const [invoiceData, setInvoiceData] = useState<ISalesSlip | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [invoiceCurrentDate, setInvoiceCurrentDate] = useState(new Date())
  const [selectedAgencyId, setSelectedAgencyId] = useState<number | null>(null)
  const [payload, setPayload] = useState<{
    page: number
    size: number
    paymentDateFrom: string
    paymentDateTo: string
    [key: string]: any
    agencyId?: number | null
  }>({
    page: 0,
    size: 10,
    paymentDateFrom: format(oneYearAgo(new Date()), 'yyyyMMdd'),
    paymentDateTo: format(new Date(), 'yyyyMMdd'),
    agencyId: null,
  })
  const [listOrgs, setListOrgs] = useState<OrganizationV2[]>([])
  const [csvData, setCsvData] = useState<
    {
      [key: string]: string | number
    }[]
  >([])
  const [cancelingPayment, setCancelingPayment] =
    useState<ProductPurchaseHistoryResponse | null>(null)
  const [relatedPayments, setRelatedPayments] = useState<Payment[]>([])
  const [listAgencies, setListAgencies] = useState<Agency[]>([])
  const {onOpen: onSuccessModalOpen} = useSuccessModal()
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const {
    listProductPurchaseHistoryOrganization,
    handleGetProductPurchaseOrganizationHistory,
    loading,
  } = useProductManagement()

  const listProductStatus = [
    {
      label: t('IAll'),
      value: 'ALL',
    },
    {
      label: t('IPaymentCompleted'),
      value: 'COMPLETED',
    },
    {
      label: t('IPurchaseCancel'),
      value: 'REFUNDED',
    },
    {
      label: t('IWriting'),
      value: 'ONGOING',
    },
  ]

  const changePaymentStatus = (value: string) => {
    setPaymentStatus(value)
    if (value === 'ALL') {
      const newPayload = {...payload}
      delete newPayload.orderStatus
      setPayload(newPayload)
    } else {
      setPayload({
        ...payload,
        orderStatus: value,
      })
    }
  }

  const handleChangeDate = (date: PeriodDate) => {
    setPayload({
      ...payload,
      paymentDateFrom: date.startDate ? format(date.startDate, 'yyyyMMdd') : '',
      paymentDateTo: date.endDate ? format(date.endDate, 'yyyyMMdd') : '',
    })
  }

  const handleSearch = (key: string, value: string) => {
    const newPayload = {...payload}
    SEARCH_SELECT_ITEMS.forEach((item) => {
      if (item.value in newPayload) {
        delete newPayload[item.value]
      }
    })
    setPayload({
      ...newPayload,
      [key]: value,
    })
  }

  const handleGetListOrgs = async () => {
    try {
      const orgIds =
        listProductPurchaseHistoryOrganization?.content
          ?.map((item) => Number(item.order.orgId))
          .filter((orgId) => Number.isFinite(orgId)) ?? []

      if (!orgIds.length) {
        setListOrgs([])
        return
      }

      const uniqueOrgIds = Array.from(new Set(orgIds))
      const resDetails = await getListOrgDetailApi({orgId: uniqueOrgIds})
      setListOrgs(
        resDetails.list
          .map((item) => item.org)
          .filter((org): org is OrganizationV2 => org !== undefined),
      )
    } catch (error) {
      setListOrgs([])
      onFailureModalOpen(error.message)
    }
  }

  const getRelatedPayment = async (paymentId: string) => {
    const res = await getRelatedPaymentApi(paymentId)
    setRelatedPayments(res.data)
  }

  const onCancelPayment = (item: ProductPurchaseHistoryResponse) => {
    getRelatedPayment(item.payment.paymentId)
    setCancelingPayment(item)
    setIsOpenCancelDialog(true)
  }

  const onSubmitInvoice = async (item: ProductPurchaseHistoryResponse) => {
    try {
      const res = await getSalesSlipApi(item.payment.paymentId)
      setInvoiceData(res.data)
      setInvoiceCurrentDate(
        res.data.issueDate ? new Date(res.data.issueDate) : new Date(),
      )
      setIsDirty(false)
      setIsShowInvoicePopup(true)
    } catch (error) {
      onFailureModalOpen(error.message)
    }
  }

  const handleUpdateInvoice = (value: string, key: string) => {
    setIsDirty(true)
    if (['orgName', 'address', 'email'].includes(key)) {
      setInvoiceData((prev) => {
        if (!prev) return null
        return {
          ...prev,
          buyer: {
            ...prev.buyer,
            [key]: value,
          },
        }
      })
    } else if (['salesSlipNo', 'remark'].includes(key)) {
      setInvoiceData((prev) => {
        if (!prev) return null
        return {
          ...prev,
          [key]: value,
        }
      })
    } else if (['name', 'quantity', 'unitPrice', 'price'].includes(key)) {
      setInvoiceData((prev) => {
        if (!prev) return null
        return {
          ...prev,
          product: {
            ...prev.product,
            [key]: key !== 'name' ? Number(value.replace(/,/g, '')) : value,
          },
        }
      })
    } else if (key === 'sellerName') {
      setInvoiceData((prev) => {
        if (!prev) return null
        return {
          ...prev,
          seller: {
            name: value,
          },
        }
      })
    }
  }

  const handleSubmitInvoice = async () => {
    try {
      const payload = {
        paymentId: invoiceData?.paymentId || '',
        salesSlipNo: invoiceData?.salesSlipNo || '',
        issueDate: formatDate(invoiceCurrentDate),
        buyer: {
          orgName: invoiceData?.buyer.orgName || '',
          address: invoiceData?.buyer.address || '',
          email: invoiceData?.buyer.email || '',
        },
        seller: {
          name: invoiceData?.seller.name || '',
        },
        product: {
          name: invoiceData?.product.name || '',
          quantity: invoiceData?.product.quantity || 0,
          unitPrice: invoiceData?.product.unitPrice || 0,
          price: invoiceData?.product.price || 0,
        },
        remark: invoiceData?.remark || '',
      }
      const res = await updateSalesSlipApi(payload)
      if (res.msg === 'SUCCESS') {
        onSuccessModalOpen(t('IProcessSuccess'))
        setIsDirty(false)
      }
    } catch (error) {
      onFailureModalOpen(error.message)
      throw error
    }
  }

  const getListAgency = async () => {
    const res = await getListAgencyApi()
    setListAgencies(res.list)
  }

  const onSelectAgency = (agencyId: number | null) => {
    setSelectedAgencyId(agencyId)
    setPayload({
      ...payload,
      agencyId,
    })
  }

  useEffect(() => {
    getListAgency()
  }, [])

  useEffect(() => {
    handleGetProductPurchaseOrganizationHistory(payload)
  }, [payload])

  useEffect(() => {
    handleGetListOrgs()
  }, [listProductPurchaseHistoryOrganization])

  return (
    <div
      style={{
        backgroundColor: '#fff',
        borderRadius: 4,
      }}
    >
      <CardWithTitle
        title={t('IProductPurchaseHistoryOrganization')}
        style={{
          border: 'none',
        }}
      >
        <TableToolbar onChangeDate={handleChangeDate} onSearch={handleSearch} />
        <div className={classes.agencyWrapper}>
          <div
            className={classes.paymentTitle}
            style={{
              width: i18n.language === 'ko' ? 62 : 117.5,
            }}
          >
            {t('IAgency')}
          </div>
          <div className={classes.agencyCardWrapper}>
            {listAgencies.map((agency) => (
              <div
                key={agency.id}
                className={`${classes.agencyCard} ${
                  agency.id === selectedAgencyId ? classes.agencyCardActive : ''
                }`}
                onClick={() =>
                  onSelectAgency(
                    agency.id === selectedAgencyId ? null : agency.id,
                  )
                }
              >
                <p>{agency.name}</p>
                <p>{agency.email}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.statusWrapper}>
          <div className={classes.paymentWrapper}>
            <div className={classes.paymentTitle}>{t('IPaymentStatus')}</div>
            <div className={classes.paymentButtonWrapper}>
              {listProductStatus.map((status) => (
                <button
                  key={status.value}
                  type='button'
                  className={`${classes.paymentButton} ${
                    status.value === paymentStatus
                      ? classes.paymentButtonActive
                      : ''
                  }`}
                  onClick={() => changePaymentStatus(status.value)}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            variant='contained'
            color='primary'
            onClick={() => downloadCsv(csvData)}
          >
            {t('IExcelConvert')}
          </Button>
        </div>
        {loading ? (
          <div
            style={{
              minHeight: 500,
              position: 'relative',
            }}
          >
            <AppLoading loading />
          </div>
        ) : (
          <DataTable
            items={listProductPurchaseHistoryOrganization?.content || []}
            listOrgs={listOrgs}
            onCancelPurchase={onCancelPayment}
            onShowInvoice={onSubmitInvoice}
            onNavigateProductDetail={(id: string) =>
              navigate(`/products-purchase-history-organization/detail/${id}`)
            }
            onUpdateCsvData={setCsvData}
            currentPage={listProductPurchaseHistoryOrganization?.number || 0}
            size={listProductPurchaseHistoryOrganization?.size || 0}
          />
        )}
        {(listProductPurchaseHistoryOrganization?.totalPages || 0) - 1 > 0 && (
          <Pagination
            totalPageCount={
              listProductPurchaseHistoryOrganization?.totalPages || 0
            }
            currentPageIndex={payload.page}
            itemCountPerPage={payload.size}
            setCurrentPageIndex={(page) => {
              setPayload({
                ...payload,
                page,
              })
            }}
            onItemCountPerPageChanged={(e) => {
              setPayload({
                ...payload,
                size: e.target.value as number,
                page: 0,
              })
            }}
            loading={loading}
          />
        )}
      </CardWithTitle>
      {isOpenCancelDialog && (
        <CancelProductDialog
          isOpen={isOpenCancelDialog}
          onClose={() => setIsOpenCancelDialog(false)}
          product={cancelingPayment}
          relatedPayments={relatedPayments ?? []}
          onCancelPayment={() =>
            handleGetProductPurchaseOrganizationHistory(payload)
          }
        />
      )}
      {isShowInvoicePopup && (
        <InvoicePopup
          open={isShowInvoicePopup}
          invoice={invoiceData}
          onSubmitInvoice={handleSubmitInvoice}
          isDirty={isDirty}
          invoiceCurrentDate={invoiceCurrentDate}
          setInvoiceCurrentDate={(date: Date) => {
            setInvoiceCurrentDate(date)
            setIsDirty(true)
          }}
          onUpdateInvoice={handleUpdateInvoice}
          handleClose={() => {
            setInvoiceData(null)
            setIsDirty(false)
            setIsShowInvoicePopup(false)
          }}
        />
      )}
    </div>
  )
}

export default ProductHistoryOrganizationList
